import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { useDebugger } from './DebuggerContext';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';
import { useWebSocketChunking } from '../hooks/useWebSocketChunking';
import { needsChunking } from '../utils/chunking';

/**
 * WebSocket message structure for all server responses.
 */
export interface WebSocketMessage {
  type: 'response' | 'error' | 'complete';
  requestId: string;
  timestamp: string;
  data?: any;
  error?: {
    code: string;
    message: string;
    details?: any;
    _debug?: any;
  };
}

/**
 * WebSocket request structure for client-to-server messages.
 */
export interface WebSocketRequest {
  action: string;
  requestId: string;
  payload: any;
}

/**
 * Handlers for progressive/streaming WebSocket responses.
 */
export interface ProgressHandlers {
  onProgress?: (data: any) => void;
  onComplete?: (data?: any) => void;
  onError?: (error: Error) => void;
}

/**
 * WebSocket context value - the unified interface for WebSocket operations.
 */
interface WebSocketContextValue {
  /** Whether the WebSocket is currently connected and ready */
  isConnected: boolean;
  /** Whether a connection attempt is in progress */
  isConnecting: boolean;
  /** Last connection error, if any */
  error: Error | null;
  /** WebSocket connectionId from WHOAMI (available after connect) */
  connectionId: string | null;
  /** Whether WebSockets are enabled via feature flag */
  isEnabled: boolean;
  /** Send a message and wait for response data */
  sendMessageAndWait: <T = any>(
    action: string,
    payload: any,
    timeoutMs?: number,
  ) => Promise<T>;
  /** Send a message with progress handlers for streaming responses */
  sendMessageWithProgressHandlers: (
    request: WebSocketRequest,
    handlers: ProgressHandlers,
    timeoutMs?: number,
  ) => Promise<void>;
  /** Register a global listener for all WebSocket messages (returns cleanup function) */
  addGlobalListener: (
    listener: (message: WebSocketMessage) => void,
  ) => () => void;
  /** Manually disconnect */
  disconnect: () => void;
  /** Manually reconnect */
  reconnect: () => void;
}

export const WebSocketContext = createContext<WebSocketContextValue | null>(
  null,
);

/** Default timeout for WebSocket operations - 15 minutes */
const DEFAULT_TIMEOUT_MS = 15 * 60 * 1000;

interface WebSocketProviderProps {
  children: ReactNode;
}

/**
 * Provider for the unified WebSocket connection.
 * Must be placed after AuthProvider, TenantProvider, and FeatureFlagsProvider.
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const { session } = useAuth();
  const { wsApi } = useTenant();
  const debug = useDebugger();
  const { hasFeature } = useFeatureFlags();

  const isEnabled = hasFeature(FeatureFlags.NGW_USE_WEBSOCKETS);

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);

  /** Reference to the active WebSocket instance */
  const wsRef = useRef<WebSocket | null>(null);

  /**
   * Send a raw message without chunking
   */
  const sendRawMessage = useCallback((message: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      try {
        wsRef.current.send(JSON.stringify(message));
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }, []);

  const { sendChunkedMessage } = useWebSocketChunking(sendRawMessage);

  /**
   * Map of pending requests awaiting responses.
   */
  const requestsRef = useRef<
    Map<
      string,
      {
        resolve: (value: WebSocketMessage) => void;
        reject: (error: Error) => void;
        timeout: NodeJS.Timeout;
        handlers?: ProgressHandlers;
      }
    >
  >(new Map());

  /**
   * Set of global listeners for all WebSocket messages.
   */
  const globalListenersRef = useRef<Set<(message: WebSocketMessage) => void>>(
    new Set(),
  );

  /** Timeout handle for reconnection attempts */
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  /** Current reconnection attempt count for exponential backoff */
  const reconnectAttemptsRef = useRef(0);

  /**
   * Establishes WebSocket connection with session-based authentication.
   */
  const connect = useCallback(() => {
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      isConnecting ||
      !isEnabled
    ) {
      return;
    }

    const sessionId = session?.id;
    if (!sessionId) {
      const error = new Error('No session found');
      setError(error);
      debug.log('WebSocket', 'No session found for connection', 'error');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const ws = new WebSocket(`${wsApi}?session=${sessionId}`);

      /**
       * Connection established - sends WHOAMI to get connectionId
       */
      ws.onopen = async () => {
        debug.log('WebSocket', 'Connection established', 'info');
        setIsConnected(true);
        setIsConnecting(false);
        setError(null);
        reconnectAttemptsRef.current = 0;

        // Send WHOAMI to get connectionId
        try {
          const whoamiRequest = {
            action: 'WHOAMI',
            requestId: `whoami-${crypto.randomUUID()}`,
            payload: {},
          };

          ws.send(JSON.stringify(whoamiRequest));

          const handleWhoamiResponse = (event: MessageEvent) => {
            try {
              const message = JSON.parse(event.data) as WebSocketMessage;
              if (
                message.requestId === whoamiRequest.requestId &&
                message.type === 'complete'
              ) {
                const newConnectionId = message.data?.connectionId;
                if (newConnectionId) {
                  setConnectionId(newConnectionId);
                  debug.log(
                    'WebSocket',
                    `WHOAMI complete - connectionId: ${newConnectionId}`,
                    'info',
                  );
                }
                ws.removeEventListener('message', handleWhoamiResponse);
              }
            } catch (err) {
              debug.log(
                'WebSocket',
                `Failed to parse WHOAMI response: ${err}`,
                'error',
              );
            }
          };

          ws.addEventListener('message', handleWhoamiResponse);
        } catch (err) {
          debug.log('WebSocket', `WHOAMI request failed: ${err}`, 'error');
        }
      };

      /**
       * Connection closed
       */
      ws.onclose = (event) => {
        debug.log(
          'WebSocket',
          `Connection closed - Code: ${event.code}, Reason: ${event.reason}`,
          'warn',
        );
        setIsConnected(false);
        setIsConnecting(false);
        setConnectionId(null);

        // Reject all pending requests
        requestsRef.current.forEach(({ reject, timeout }) => {
          clearTimeout(timeout);
          reject(new Error('WebSocket connection closed'));
        });
        requestsRef.current.clear();

        // Reconnection logic
        if (event.code !== 1000 && reconnectAttemptsRef.current < 5) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttemptsRef.current),
            30000,
          );
          debug.log(
            'WebSocket',
            `Scheduling reconnect attempt ${reconnectAttemptsRef.current + 1} in ${delay}ms`,
            'info',
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        }
      };

      /**
       * Connection error
       */
      ws.onerror = () => {
        debug.log('WebSocket', 'Connection error occurred', 'error');
        setError(new Error('WebSocket connection error'));
      };

      /**
       * Message received from server
       */
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;

          // Notify global listeners FIRST
          globalListenersRef.current.forEach((listener) => {
            try {
              listener(message);
            } catch (err) {
              debug.log('WebSocket', `Global listener error: ${err}`, 'error');
            }
          });

          // Handle request-response correlation
          const request = requestsRef.current.get(message.requestId);

          if (request) {
            if (message.type === 'error') {
              clearTimeout(request.timeout);
              requestsRef.current.delete(message.requestId);

              debug.log(
                'WebSocket',
                `Request ${message.requestId} failed: ${message.error?.message}`,
                'error',
              );

              const error = new Error(
                message.error?.message || 'Operation failed',
              );
              (error as any).code = message.error?.code;
              (error as any).details = message.error?.details;
              (error as any)._debug = message.error?._debug;

              if (request.handlers?.onError) {
                request.handlers.onError(error);
              }

              request.reject(error);
            } else if (message.type === 'complete') {
              clearTimeout(request.timeout);
              requestsRef.current.delete(message.requestId);

              if (request.handlers?.onComplete) {
                request.handlers.onComplete(message.data);
              }

              request.resolve(message);
            } else if (message.type === 'response') {
              if (request.handlers?.onProgress) {
                request.handlers.onProgress(message.data);
              }
            }
          }
        } catch (err) {
          debug.log('WebSocket', `Failed to parse message: ${err}`, 'error');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      debug.log('WebSocket', `Failed to create connection: ${err}`, 'error');
      setError(err as Error);
      setIsConnecting(false);
    }
  }, [wsApi, session?.id, debug, isEnabled]);

  /**
   * Gracefully disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }
  }, []);

  /**
   * Send a message and wait for response data
   */
  const sendMessageAndWait = useCallback(
    async <T = any,>(
      action: string,
      payload: any,
      timeoutMs: number = DEFAULT_TIMEOUT_MS,
    ): Promise<T> => {
      return new Promise((resolve, reject) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          reject(new Error('WebSocket is not connected'));
          return;
        }

        const request: WebSocketRequest = {
          action,
          requestId: crypto.randomUUID(),
          payload,
        };

        const messageStr = JSON.stringify(request);

        const timeout = setTimeout(() => {
          requestsRef.current.delete(request.requestId);
          reject(new Error(`Request timeout after ${timeoutMs}ms`));
        }, timeoutMs);

        requestsRef.current.set(request.requestId, {
          resolve: (message: WebSocketMessage) => {
            if (message.type === 'complete' && message.data !== undefined) {
              resolve(message.data as T);
            } else {
              reject(new Error('No data received in response'));
            }
          },
          reject,
          timeout,
        });

        const sendPromise = needsChunking(messageStr)
          ? sendChunkedMessage(request, {})
          : sendRawMessage(request);
        sendPromise.catch((err) => {
          clearTimeout(timeout);
          requestsRef.current.delete(request.requestId);
          reject(err);
        });
      });
    },
    [sendRawMessage, sendChunkedMessage],
  );

  /**
   * Send a message with progress handlers
   */
  const sendMessageWithProgressHandlers = useCallback(
    (
      request: WebSocketRequest,
      handlers: ProgressHandlers,
      timeoutMs: number = DEFAULT_TIMEOUT_MS,
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          const error = new Error('WebSocket is not connected');
          if (handlers.onError) handlers.onError(error);
          reject(error);
          return;
        }

        const messageStr = JSON.stringify(request);

        const timeout = setTimeout(() => {
          requestsRef.current.delete(request.requestId);
          const error = new Error(`Request timeout after ${timeoutMs}ms`);
          if (handlers.onError) handlers.onError(error);
          reject(error);
        }, timeoutMs);

        requestsRef.current.set(request.requestId, {
          resolve: () => resolve(),
          reject,
          timeout,
          handlers,
        });

        const sendAsync = async () => {
          try {
            if (needsChunking(messageStr)) {
              await sendChunkedMessage(request, {});
            } else {
              await sendRawMessage(request);
            }
          } catch (err) {
            clearTimeout(timeout);
            requestsRef.current.delete(request.requestId);
            const error =
              err instanceof Error ? err : new Error('Failed to send message');
            if (handlers.onError) handlers.onError(error);
            reject(error);
          }
        };

        sendAsync();
      });
    },
    [sendRawMessage, sendChunkedMessage],
  );

  /**
   * Register a global listener for all WebSocket messages
   */
  const addGlobalListener = useCallback(
    (listener: (message: WebSocketMessage) => void) => {
      globalListenersRef.current.add(listener);
      return () => {
        globalListenersRef.current.delete(listener);
      };
    },
    [],
  );

  /**
   * Connect when enabled and session is available
   */
  useEffect(() => {
    if (isEnabled && session?.id && wsApi && !wsRef.current) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [session?.id, wsApi, isEnabled, connect]);

  const value: WebSocketContextValue = {
    isConnected,
    isConnecting,
    error,
    connectionId,
    isEnabled,
    sendMessageAndWait,
    sendMessageWithProgressHandlers,
    addGlobalListener,
    disconnect,
    reconnect: connect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to access the unified WebSocket connection.
 * Must be used within a WebSocketProvider.
 */
export const useWebSocketContext = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within a WebSocketProvider',
    );
  }
  return context;
};
